'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import { ApiInitiative } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MediaSelector } from '@/components/ui/media-selector';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InitiativeTypeSelector } from '@/components/initiatives/InitiativeTypeSelector';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, MapPin, Settings2, X } from 'lucide-react';
import { format, setHours, setMinutes } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { ActivationSettingsForm } from '@/components/initiatives/ActivationSettingsForm';
import { DemographicsSection } from '@/components/initiatives/DemographicsSection';
import type {
  ActivationSettings,
  AttendanceSettings,
  ObjectComponent,
  InitiativeDemographics,
} from '@sizzlenz/shared';
import { CalendarLookup } from '@/components/lookups/CalendarLookup';
import { SearchRecordLookup } from '@/components/lookups/SearchRecordLookup';
import { ObjectOwnerLookup } from '@/components/lookups/ObjectOwnerLookup';
import { HubLinker, parseHubIdsFromSystemTags } from '@/components/lookups/HubLinker';
import type { ApiContent } from '@/lib/api/types';
import type { LookupOption } from '@/components/ui/lookup';
import type { ApiCalendar, ApiSearchObject } from '@/lib/api/types';
import { MultiSelectDropdown } from '@/components/ui/multi-select-dropdown';
import {
  INITIATIVE_CATEGORY_OPTIONS,
  INITIATIVE_TAG_OPTIONS,
  INITIATIVE_STAGE_OPTIONS,
  INITIATIVE_PROPOSAL_STATUS_OPTIONS,
  INITIATIVE_OCCASION_OPTIONS,
  INITIATIVE_THEME_OPTIONS,
  PRODUCT_CATEGORY_OPTIONS,
  type InitiativeType,
} from '@sizzlenz/shared';
import { ImageGalleryManager } from '@/components/ui/image-gallery-manager';
import { PlaceAutocomplete } from '@/components/Maps/PlaceAutocomplete';
import { ObjectComponentsEditor } from '@/components/object-components/ObjectComponentsEditor';
import { useCreateInitiative, useUpdateInitiative } from '@/lib/hooks/useInitiatives';
import { useInitiativeDetailSheet } from '@/lib/hooks/useInitiativeDetailSheet';
import { Eye } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useForm, Controller } from 'react-hook-form';
import { RichTextEditor } from '@/components/ui/rich-text-editor';

type AccessVisibility = 'private' | 'public' | 'user_trade_partners' | 'approved_partners';

const ACCESS_OPTIONS: { value: AccessVisibility; label: string; description: string }[] = [
  { value: 'private', label: 'Private', description: 'Hidden from everyone' },
  { value: 'public', label: 'Public', description: 'Anyone can view' },
  {
    value: 'user_trade_partners',
    label: 'Trade Partner Users',
    description: 'Users with trade partner status can view',
  },
  {
    value: 'approved_partners',
    label: 'Approved Partners',
    description: 'Only approved partners can view',
  },
];

const DEFAULT_TIMEFRAME_OPTIONS: { value: string; label: string }[] = [
  { value: 'right_now', label: 'Right Now' },
  { value: 'next_few_days', label: 'Next Few Days' },
  { value: 'next_couple_weeks', label: 'Next Couple of Weeks' },
  { value: 'next_month', label: 'Next Month' },
];

function AccessSelect({
  value,
  onChange,
  label = 'Visible To',
}: {
  value: AccessVisibility;
  onChange: (value: AccessVisibility) => void;
  label?: string;
}) {
  const selected = ACCESS_OPTIONS.find((o) => o.value === value);
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={(v) => onChange(v as AccessVisibility)}>
        <SelectTrigger>
          <SelectValue placeholder="Select visibility" />
        </SelectTrigger>
        <SelectContent>
          {ACCESS_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected && <p className="text-sm text-gray-500 mt-1">{selected.description}</p>}
    </div>
  );
}

interface InitiativeFormData {
  name: string;
  description: string;
  content: string;
  type: InitiativeType;
  status: string;
  proposalStatus: string;
  visibility: string;
  image: string | null;
  tags: string;
  categories: string[]; // is actually Audience, to be refactored in the future
  productCategories: string[];
  activities: string[];
  occasions: string[];
  themes: string[];
  themeFeatured: boolean;
  featured: boolean;
  isTemplate: boolean;
  blurb: string;
  startDate: Date | undefined;
  endDate: Date | undefined;
  startTime: string;
  endTime: string;
  galleryImages: string[];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactWebsite: string;
  hideContactDetails: boolean;
  contactVisibility: AccessVisibility;
  anonymous: boolean;
  address_1: Record<string, unknown> | null;
  showAddress: boolean;
  showMap: boolean;
  activationSettings: Partial<ActivationSettings>;
  ownerType: string;
  ownerId: string;
  components: ObjectComponent[];
  allowReactions: boolean;
  allowComments: boolean;
  votingEnabled: boolean;
  interestMode: 'disabled' | 'simple' | 'structured';
  structuredInterestConfig: {
    enableEntitySelection: boolean;
    requireEntity: boolean;
    allowNote: boolean;
    requireNote: boolean;
    allowMultipleEntities: boolean;
    enableBrandField: boolean;
    requireBrandField: boolean;
    brandFieldLabel: string;
    enableTimeframe: boolean;
    requireTimeframe: boolean;
    timeframeLabel: string;
    timeframeOptions: { value: string; label: string }[];
    dialogTitle?: string;
    dialogDescription?: string;
  };
  specifyDemographics: boolean;
  demographics: InitiativeDemographics;
  demographicsVisibility: 'public' | 'private' | 'approved_viewers';
  attendanceSettings: AttendanceSettings;
  relatedContentEnabled: boolean;
  relatedContentTags: string;
  relatedContentTitle: string;
  relatedContentBlurb: string;
}

interface InitiativeFormProps {
  initialData?: ApiInitiative;
  ownerId?: string;
  ownerType?: string;
  isAdmin?: boolean;
  calendarSupport?: 'none' | 'single' | 'multiple';
  fixedType?: string;
  defaultType?: string;
  onSuccess?: (initiative: ApiInitiative) => void;
  onCancel?: () => void;
}

export function InitiativeForm({
  initialData,
  ownerId,
  ownerType = 'entity',
  calendarSupport = 'none',
  fixedType,
  defaultType,
  onSuccess,
  onCancel,
}: InitiativeFormProps) {
  const isEditing = !!initialData?.id;

  const createMutation = useCreateInitiative();
  const updateMutation = useUpdateInitiative();
  const { showInitiativeDetail } = useInitiativeDetailSheet();

  const { register, handleSubmit, setValue, watch, control } = useForm<InitiativeFormData>({
    defaultValues: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      content: initialData?.content || '',
      type: (fixedType || defaultType || initialData?.type || 'campaign') as InitiativeType,
      status: initialData?.status || 'draft',
      proposalStatus: initialData?.proposalStatus || '',
      visibility: initialData?.visibility || 'private',
      image: (initialData as any)?.image || null,
      tags: initialData?.tags?.join(', ') || '',
      // is actually Audience, to be refactored in the future
      categories: (initialData?.data as any)?.categories || [],
      productCategories: (initialData?.data as any)?.productCategories || [],
      activities: (initialData?.data as any)?.activities || [],
      occasions: (initialData?.data as any)?.occasions || [],
      themes: (initialData?.data as any)?.themes || [],
      themeFeatured: (initialData?.data as any)?.themeFeatured || false,
      featured: (initialData?.systemTags || []).includes('@featured=true'),
      isTemplate: initialData?.isTemplate || false,
      blurb: (initialData?.data as any)?.blurb || '',
      startDate: initialData?.startDate ? new Date(initialData.startDate) : undefined,
      endDate: initialData?.endDate ? new Date(initialData.endDate) : undefined,
      startTime: initialData?.startDate
        ? format(new Date(initialData.startDate), 'HH:mm')
        : '00:00',
      endTime: initialData?.endDate ? format(new Date(initialData.endDate), 'HH:mm') : '23:59',
      galleryImages: (initialData?.data as any)?.gallery?.images || [],
      contactName: (initialData?.data as any)?.contact?.name || '',
      contactEmail: (initialData?.data as any)?.contact?.email || '',
      contactPhone: (initialData?.data as any)?.contact?.phone || '',
      contactWebsite: (initialData?.data as any)?.contact?.website || '',
      hideContactDetails: (initialData?.data as any)?.contact?.hideDetails || false,
      contactVisibility: (initialData?.data as any)?.contact?.visibility || 'public',
      anonymous: (initialData?.data as any)?.anonymous?.enabled || false,
      address_1: (initialData?.data as any)?.address_1 || null,
      showAddress: (initialData?.data as any)?.geo?.showAddress ?? true,
      showMap: (initialData?.data as any)?.geo?.showMap ?? true,
      activationSettings: (initialData?.data as any)?.activation || {},
      ownerType: initialData?.ownerType || ownerType || 'entity',
      ownerId: initialData?.ownerId || ownerId || '',
      components: (initialData?.data as any)?.components || [],
      allowReactions: (initialData?.data as any)?.allowReactions !== false,
      allowComments: (initialData?.data as any)?.allowComments !== false,
      votingEnabled: (initialData?.data as any)?.votingEnabled || false,
      interestMode:
        (initialData?.data as any)?.interestMode ||
        // Migrate from old votingMode: if votingMode was 'interest', default to 'simple'
        ((initialData?.data as any)?.votingMode === 'interest' ? 'simple' : 'disabled'),
      structuredInterestConfig: {
        enableEntitySelection: true,
        requireEntity: true,
        allowNote: true,
        requireNote: false,
        allowMultipleEntities: true,
        enableBrandField: false,
        requireBrandField: false,
        brandFieldLabel: '',
        enableTimeframe: false,
        requireTimeframe: false,
        timeframeLabel: '',
        timeframeOptions: DEFAULT_TIMEFRAME_OPTIONS,
        dialogTitle: '',
        dialogDescription: '',
        ...((initialData?.data as any)?.structuredInterestConfig || {}),
      },
      specifyDemographics: !!(initialData?.data as any)?.demographics,
      demographics: (initialData?.data as any)?.demographics || {
        numberOfAttendees: undefined,
        ageGroups: [],
        gender: '',
        genderSplit: { male: 33, female: 33, other: 34 },
      },
      demographicsVisibility: (initialData?.data as any)?.demographics?.visibility || 'public',
      attendanceSettings: (initialData?.data as any)?.attendance || {
        enabled: false,
        requiresApproval: false,
        capacity: null,
      },
      relatedContentEnabled: !!(initialData?.data as any)?.relatedContent?.enabled,
      relatedContentTags: ((initialData?.data as any)?.relatedContent?.tags || []).join(', '),
      relatedContentTitle: (initialData?.data as any)?.relatedContent?.title || '',
      relatedContentBlurb: (initialData?.data as any)?.relatedContent?.blurb || '',
    },
  });

  // Owner lookup state for displaying the selected owner
  const [selectedOwner, setSelectedOwner] = useState<LookupOption<ApiSearchObject> | null>(() => {
    if (initialData?.owner) {
      return {
        id: initialData.owner.objectId,
        label: initialData.owner.name,
        data: {
          id: initialData.owner.id,
          objectType: initialData.owner.objectType,
          objectId: initialData.owner.objectId,
          name: initialData.owner.name,
          image: initialData.owner.image,
        } as unknown as ApiSearchObject,
      };
    }
    return null;
  });

  // Watch values needed for conditional rendering
  const type = watch('type');
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const image = watch('image');
  const contactEmail = watch('contactEmail');
  const address_1 = watch('address_1');
  const showAddress = watch('showAddress');
  const showMap = watch('showMap');
  const activationSettings = watch('activationSettings');

  // UI-only state (not form data)
  const [activationDialogOpen, setActivationDialogOpen] = useState(false);

  // Calendar support state (not part of form data, managed separately)
  const [selectedCalendar, setSelectedCalendar] = useState<LookupOption<ApiCalendar> | null>(null);
  const [selectedCalendars, setSelectedCalendars] = useState<LookupOption<ApiCalendar>[]>([]);
  const calendarsInitialized = React.useRef(false);

  // Linked records state (object record links via SearchRecordLookup)
  const [selectedLinkedRecords, setSelectedLinkedRecords] = useState<
    LookupOption<ApiSearchObject>[]
  >([]);
  const linkedRecordsInitialized = React.useRef(false);

  // Linked hubs state (hub content records)
  const [selectedHubs, setSelectedHubs] = useState<LookupOption<ApiContent>[]>([]);
  const hubsInitialized = React.useRef(false);

  // Extract calendar IDs from systemTags
  const calendarIds = React.useMemo(() => {
    if (!initialData?.systemTags || calendarSupport === 'none') {
      return [];
    }
    const calendarTags = initialData.systemTags.filter((tag) => tag.startsWith('@calendar='));
    return calendarTags.map((tag) => parseInt(tag.replace('@calendar=', '')));
  }, [initialData?.systemTags, calendarSupport]);

  // Extract linked record references from systemTags (format: @linked=type:id)
  const linkedRecordRefs = React.useMemo(() => {
    if (!initialData?.systemTags) {
      return [];
    }
    return initialData.systemTags
      .filter((tag) => tag.startsWith('@linked='))
      .map((tag) => {
        const value = tag.replace('@linked=', '');
        const [objectType, objectId] = value.split(':');
        return { objectType, objectId };
      })
      .filter((ref) => ref.objectType && ref.objectId);
  }, [initialData?.systemTags]);

  // Extract hub IDs from systemTags (format: @hub=<id>)
  const hubIds = React.useMemo(
    () => parseHubIdsFromSystemTags(initialData?.systemTags),
    [initialData?.systemTags]
  );

  // For editing, we need to fetch the initial calendars to populate the selected state
  React.useEffect(() => {
    if (!calendarIds.length || calendarsInitialized.current || !isEditing) {
      return;
    }

    const fetchInitialCalendars = async () => {
      try {
        const { calendarApi } = await import('@/lib/api/endpoints');
        const calendars = await Promise.all(calendarIds.map((id) => calendarApi.getCalendar(id)));

        const calendarOptions: LookupOption<ApiCalendar>[] = calendars.map((cal) => ({
          id: cal.id,
          label: cal.name,
          data: cal,
        }));

        if (calendarSupport === 'single') {
          setSelectedCalendar(calendarOptions[0]);
        } else if (calendarSupport === 'multiple') {
          setSelectedCalendars(calendarOptions);
        }

        calendarsInitialized.current = true;
      } catch (error) {
        console.error('Failed to load initial calendars:', error);
        toast.error('Failed to load linked calendars');
      }
    };

    fetchInitialCalendars();
  }, [calendarIds, calendarSupport, isEditing]);

  // For editing, fetch initial linked records to populate the selected state
  React.useEffect(() => {
    if (!linkedRecordRefs.length || linkedRecordsInitialized.current || !isEditing) {
      return;
    }

    // Reconstruct linked record options from data.linkedRecords display cache
    const linkedRecordsCache = (initialData?.data as any)?.linkedRecords as
      | Array<{ objectType: string; objectId: number; name: string; image?: string | null }>
      | undefined;

    if (linkedRecordsCache && linkedRecordsCache.length > 0) {
      const linkedOptions: LookupOption<ApiSearchObject>[] = linkedRecordsCache.map((record) => ({
        id: `${record.objectType}:${record.objectId}`,
        label: record.name || `${record.objectType} ${record.objectId}`,
        data: {
          id: 0,
          objectType: record.objectType,
          objectId: record.objectId,
          name: record.name,
          image: record.image || null,
        } as unknown as ApiSearchObject,
      }));
      setSelectedLinkedRecords(linkedOptions);
    }

    linkedRecordsInitialized.current = true;
  }, [linkedRecordRefs, isEditing, initialData]);

  // For editing, reconstruct linked hubs from data.hubs display cache
  React.useEffect(() => {
    if (!hubIds.length || hubsInitialized.current || !isEditing) {
      return;
    }

    const hubsCache = (initialData?.data as any)?.hubs as
      | Array<{ id: number; title: string; slug?: string }>
      | undefined;

    if (hubsCache && hubsCache.length > 0) {
      const hubOptions: LookupOption<ApiContent>[] = hubsCache.map((hub) => ({
        id: hub.id,
        label: hub.title || `Hub ${hub.id}`,
        data: { id: hub.id, title: hub.title, slug: hub.slug } as unknown as ApiContent,
      }));
      setSelectedHubs(hubOptions);
    }

    hubsInitialized.current = true;
  }, [hubIds, isEditing, initialData]);

  const mergeDateAndTime = (date: Date | undefined, time: string): string | null => {
    if (!date) return null;
    const [hours, minutes] = time.split(':').map(Number);
    const dateWithTime = setMinutes(setHours(date, hours), minutes);
    return dateWithTime.toISOString();
  };

  const handlePreview = () => {
    const values = watch();
    const finalStartDate = mergeDateAndTime(values.startDate, values.startTime);
    const finalEndDate = mergeDateAndTime(values.endDate, values.endTime);

    const previewInitiative: any = {
      id: initialData?.id || 0,
      name: values.name || 'Untitled Initiative',
      description: values.description,
      content: values.content,
      type: values.type as any,
      status: values.status,
      proposalStatus: values.proposalStatus,
      visibility: values.visibility,
      image: values.image,
      tags: values.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
      startDate: finalStartDate,
      endDate: finalEndDate,
      data: {
        ...(initialData?.data || {}),
        blurb: values.blurb,
        categories: values.categories,
        productCategories: values.productCategories,
        activities: values.activities,
        occasions: values.occasions,
        themes: values.themes,
        themeFeatured: values.themeFeatured,
        anonymous: { enabled: values.anonymous },
        address_1: values.address_1,
        geo: { showAddress: values.showAddress, showMap: values.showMap },
        gallery: { images: values.galleryImages },
        contact: {
          name: values.contactName || undefined,
          email: values.contactEmail || undefined,
          phone: values.contactPhone || undefined,
          website: values.contactWebsite || undefined,
          hideDetails: values.hideContactDetails,
          visibility: values.contactVisibility,
        },
        ...(values.type === 'activation' ? { activation: values.activationSettings } : {}),
        components: values.components,
        linkedRecords: selectedLinkedRecords.map((r) => ({
          objectType: r.data.objectType,
          objectId: r.data.objectId,
          name: r.data.name,
          image: r.data.image,
        })),
        hubs: selectedHubs.map((h) => ({
          id: h.id as number,
          title: h.label,
          slug: h.data?.slug,
        })),
        allowReactions: values.allowReactions,
        allowComments: values.allowComments,
        votingEnabled: values.votingEnabled,
        interestMode: values.interestMode,
        structuredInterestConfig:
          values.interestMode === 'structured' ? values.structuredInterestConfig : undefined,
        ...(values.specifyDemographics
          ? { demographics: { ...values.demographics, visibility: values.demographicsVisibility } }
          : {}),
        ...(values.relatedContentEnabled
          ? {
              relatedContent: {
                enabled: true,
                tags: values.relatedContentTags
                  .split(',')
                  .map((t) => t.trim())
                  .filter((t) => t.length > 0),
                title: values.relatedContentTitle || undefined,
                blurb: values.relatedContentBlurb || undefined,
              },
            }
          : { relatedContent: null }),
      },
      systemData: initialData?.systemData || {},
      systemTags: initialData?.systemTags || [],
      deleted: false,
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      ownerId: ownerId || initialData?.ownerId || '',
      ownerType: ownerType || initialData?.ownerType || 'entity',
      owner: initialData?.owner || null,
    };

    showInitiativeDetail(previewInitiative, { showParticipationAction: false });
  };

  const onFormSubmit = async (data: InitiativeFormData) => {
    if (!data.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      const finalStartDate = mergeDateAndTime(data.startDate, data.startTime);
      const finalEndDate = mergeDateAndTime(data.endDate, data.endTime);

      // Build calendar IDs array based on calendar support mode
      let calIds: number[] = [];
      if (calendarSupport === 'single' && selectedCalendar) {
        calIds = [selectedCalendar.id as number];
      } else if (calendarSupport === 'multiple') {
        calIds = selectedCalendars.map((cal) => cal.id as number);
      }

      const payload: any = {
        name: data.name,
        description: data.description,
        content: data.content,
        type: data.type,
        status: data.status,
        proposalStatus: data.proposalStatus,
        visibility: data.visibility,
        image: data.image,
        isTemplate: data.isTemplate,
        featured: data.featured,
        startDate: finalStartDate,
        endDate: finalEndDate,
        tags: data.tags
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t.length > 0),
        data: {
          ...(initialData?.data || {}),
          blurb: data.blurb,
          categories: data.categories,
          productCategories: data.productCategories,
          activities: data.activities,
          occasions: data.occasions,
          themes: data.themes,
          themeFeatured: data.themeFeatured,
          anonymous: { enabled: data.anonymous },
          address_1: data.address_1,
          geo: { showAddress: data.showAddress, showMap: data.showMap },
          gallery: { images: data.galleryImages },
          contact: {
            name: data.contactName || undefined,
            email: data.contactEmail || undefined,
            phone: data.contactPhone || undefined,
            website: data.contactWebsite || undefined,
            hideDetails: data.hideContactDetails,
            visibility: data.contactVisibility,
          },
          ...(data.type === 'activation' ? { activation: data.activationSettings } : {}),
          components: data.components,
          linkedRecords: selectedLinkedRecords.map((r) => ({
            objectType: r.data.objectType,
            objectId: r.data.objectId,
            name: r.data.name,
            image: r.data.image,
          })),
          hubs: selectedHubs.map((h) => ({
            id: h.id as number,
            title: h.label,
            slug: h.data?.slug,
          })),
          allowReactions: data.allowReactions,
          allowComments: data.allowComments,
          votingEnabled: data.votingEnabled,
          interestMode: data.interestMode,
          structuredInterestConfig:
            data.interestMode === 'structured' ? data.structuredInterestConfig : undefined,
          attendance: data.attendanceSettings.enabled
            ? data.attendanceSettings
            : { enabled: false, requiresApproval: false, capacity: null },
          ...(data.specifyDemographics
            ? { demographics: { ...data.demographics, visibility: data.demographicsVisibility } }
            : { demographics: null }),
          ...(data.relatedContentEnabled
            ? {
                relatedContent: {
                  enabled: true,
                  tags: data.relatedContentTags
                    .split(',')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0),
                  title: data.relatedContentTitle || undefined,
                  blurb: data.relatedContentBlurb || undefined,
                },
              }
            : { relatedContent: null }),
        },
        calendarIds: calIds,
        linkedRecords: selectedLinkedRecords.map((r) => ({
          objectType: r.data.objectType,
          objectId: Number(r.data.objectId),
        })),
        categories: data.categories,
        activities: data.activities,
        themes: data.themes,
        themeFeatured: data.themeFeatured,
        hubIds: selectedHubs.map((h) => h.id as number),
      };

      let result;
      if (!isEditing) {
        if (ownerId && ownerType) {
          payload.ownerId = ownerId;
          payload.ownerType = ownerType;
        }
        result = await createMutation.mutateAsync(payload);
        toast.success('Initiative created');
      } else {
        // Include owner fields when editing
        payload.ownerId = data.ownerId;
        payload.ownerType = data.ownerType;
        result = await updateMutation.mutateAsync({ id: initialData.id, data: payload });
        toast.success('Initiative updated');
      }

      onSuccess?.(result);
    } catch (err: any) {
      toast.error(err.message || 'Failed to save initiative');
    }
  };

  const saveMutation = isEditing ? updateMutation : createMutation;

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6 max-w-4xl">
      <div className="bg-white rounded-xl border p-6 space-y-6">
        {/* Row 1: Name (full width) */}
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            {...register('name', { required: true })}
            placeholder="e.g. Summer Super Giveaway"
            required
          />
        </div>

        {/* Row 2: Type */}
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <InitiativeTypeSelector
              value={field.value}
              onValueChange={field.onChange}
              disabled={!!fixedType}
              showDescription={false}
            />
          )}
        />
        {/* Template Switch */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="isTemplate">Template</Label>
            <p className="text-sm text-muted-foreground">
              Mark as a reusable template that others can use as a starterNow
            </p>
          </div>
          <Controller
            name="isTemplate"
            control={control}
            render={({ field }) => (
              <Switch id="isTemplate" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>

        {/* Featured Switch */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="featured">Featured</Label>
            <p className="text-sm text-muted-foreground">
              Feature this initiative in discovery sections and starters
            </p>
          </div>
          <Controller
            name="featured"
            control={control}
            render={({ field }) => (
              <Switch id="featured" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>

        {/* Status & Proposal Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {INITIATIVE_STAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="proposalStatus">Proposal Status</Label>
            <Controller
              name="proposalStatus"
              control={control}
              render={({ field }) => (
                <Select value={field.value || undefined} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="<Not specified>" />
                  </SelectTrigger>
                  <SelectContent>
                    {INITIATIVE_PROPOSAL_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Owner */}
        {(isEditing || initialData || ownerType === 'global') && (
          <div className="space-y-2">
            <Label>Owner</Label>
            <ObjectOwnerLookup
              value={selectedOwner}
              onChange={(option) => {
                setSelectedOwner(option);
                if (option?.data) {
                  setValue('ownerType', option.data.objectType);
                  setValue('ownerId', String(option.data.objectId));
                } else {
                  setValue('ownerType', ownerType || 'entity');
                  setValue('ownerId', ownerId || '');
                }
              }}
              objectType={watch('ownerType')}
              objectId={watch('ownerId')}
              placeholder="Search for owner..."
            />
          </div>
        )}

        {/* Anonymous Switch */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="anonymous">Anonymous</Label>
            <p className="text-sm text-muted-foreground">Hide owner information from public view</p>
          </div>
          <Controller
            name="anonymous"
            control={control}
            render={({ field }) => (
              <Switch id="anonymous" checked={field.value} onCheckedChange={field.onChange} />
            )}
          />
        </div>

        {/* Activation Settings Button */}
        {['activation', 'event'].includes(type) && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setActivationDialogOpen(true)}
              className="cursor-pointer"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Edit Event / Activation Details
            </Button>

            <Dialog open={activationDialogOpen} onOpenChange={setActivationDialogOpen}>
              <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Activation Settings</DialogTitle>
                </DialogHeader>
                <ActivationSettingsForm
                  initialValue={activationSettings}
                  onChange={(val) => setValue('activationSettings', val)}
                />
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" className="cursor-pointer">
                      Done
                    </Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
        {/* Blurb */}
        <div className="space-y-2">
          <Label htmlFor="blurb">Blurb</Label>
          <Input
            id="blurb"
            {...register('blurb')}
            placeholder="A short one-liner for previews and cards"
          />
        </div>

        {/* Row 3: Short Description (full width) */}
        <div className="space-y-2">
          <Label htmlFor="description">Short Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="A brief summary for cards and lists..."
            rows={3}
          />
        </div>

        {/* Row 4: Tags and Visibility */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="tags">Tags (comma separated)</Label>
            <Input id="tags" {...register('tags')} placeholder="summer, promo, exclusive" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="visibility">Visibility</Label>
            <Controller
              name="visibility"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internal">Internal (team only)</SelectItem>
                    <SelectItem value="public">Public (visible to everyone)</SelectItem>
                    <SelectItem value="trade">Trade (Trade partners + team )</SelectItem>
                    <SelectItem value="private">Private (invite / link only)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Row 5: Audience and Occasions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Audience</Label>
            <Controller
              name="categories"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label={null}
                  placeholder="Select categories..."
                  options={INITIATIVE_CATEGORY_OPTIONS}
                  selectedIds={field.value}
                  onSelectionChange={field.onChange}
                  showChips={true}
                  showSummaryInTrigger={true}
                  showBulkActions={false}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>Occasion</Label>
            <Controller
              name="occasions"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label={null}
                  placeholder="Select occasions..."
                  options={INITIATIVE_OCCASION_OPTIONS}
                  selectedIds={field.value}
                  onSelectionChange={field.onChange}
                  showChips={true}
                  showSummaryInTrigger={true}
                  showBulkActions={false}
                />
              )}
            />
          </div>
        </div>

        {/* Product Categories & Activities */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Product Categories</Label>
            <Controller
              name="productCategories"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label={null}
                  placeholder="Select product categories..."
                  options={PRODUCT_CATEGORY_OPTIONS}
                  selectedIds={field.value}
                  onSelectionChange={field.onChange}
                  showChips={true}
                  showSummaryInTrigger={true}
                  showBulkActions={false}
                />
              )}
            />
          </div>

          <div className="space-y-2">
            <Label>Activities</Label>
            <Controller
              name="activities"
              control={control}
              render={({ field }) => (
                <MultiSelectDropdown
                  label={null}
                  placeholder="Select activities..."
                  options={INITIATIVE_TAG_OPTIONS.filter((a) => a.scopes?.includes('activity'))}
                  selectedIds={field.value}
                  onSelectionChange={field.onChange}
                  showChips={true}
                  showSummaryInTrigger={true}
                  showBulkActions={false}
                />
              )}
            />
          </div>
        </div>

        {/* Themes */}
        <div className="space-y-2">
          <Label>Themes</Label>
          <Controller
            name="themes"
            control={control}
            render={({ field }) => (
              <MultiSelectDropdown
                label={null}
                placeholder="Select themes..."
                options={INITIATIVE_THEME_OPTIONS}
                selectedIds={field.value}
                onSelectionChange={field.onChange}
                showChips={true}
                showSummaryInTrigger={true}
                showBulkActions={false}
              />
            )}
          />
          <div className="flex items-center justify-between pt-2">
            <div className="space-y-0.5">
              <Label htmlFor="themeFeatured">Featured in Theme Page</Label>
              <p className="text-sm text-muted-foreground">
                Show this initiative prominently on its theme page
              </p>
            </div>
            <Controller
              name="themeFeatured"
              control={control}
              render={({ field }) => (
                <Switch id="themeFeatured" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>
        </div>

        {/* Row 6: Start and End Date/Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Start Date & Time (Optional)</Label>
            <div className="flex gap-2">
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'flex-1 justify-start text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              <Input
                type="time"
                {...register('startTime')}
                className="w-32"
                disabled={!startDate}
              />
              {startDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setValue('startDate', undefined);
                    setValue('startTime', '00:00');
                  }}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label>End Date & Time (Optional)</Label>
            <div className="flex gap-2">
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          'flex-1 justify-start text-left font-normal',
                          !field.value && 'text-muted-foreground'
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {field.value ? format(field.value, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                        disabled={(date) => (startDate ? date < startDate : false)}
                      />
                    </PopoverContent>
                  </Popover>
                )}
              />
              <Input type="time" {...register('endTime')} className="w-32" disabled={!endDate} />
              {endDate && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setValue('endDate', undefined);
                    setValue('endTime', '23:59');
                  }}
                  className="shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Row 7: Calendar Selection (full width if present) */}
        {calendarSupport === 'single' && (
          <div className="space-y-2">
            <Label>Calendar (Optional)</Label>
            <CalendarLookup
              value={selectedCalendar}
              onChange={setSelectedCalendar}
              ownerId={ownerId}
              ownerType={ownerType}
              placeholder="Select a calendar..."
            />
            <p className="text-sm text-muted-foreground">Link this initiative to a calendar</p>
          </div>
        )}

        {calendarSupport === 'multiple' && (
          <div className="space-y-2">
            <Label>Include in Calendars</Label>
            <CalendarLookup
              value={null}
              onChange={(calendar) => {
                if (calendar && !selectedCalendars.find((c) => c.id === calendar.id)) {
                  setSelectedCalendars([...selectedCalendars, calendar]);
                }
              }}
              ownerId={ownerId}
              ownerType={ownerType}
              placeholder="Add calendars..."
            />
            {selectedCalendars.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedCalendars.map((calendar) => (
                  <div
                    key={calendar.id}
                    className="flex items-center gap-2 bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-sm"
                  >
                    <span>{calendar.label}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setSelectedCalendars(selectedCalendars.filter((c) => c.id !== calendar.id))
                      }
                      className="hover:text-destructive cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-sm text-muted-foreground">
              Link this initiative to multiple calendars
            </p>
          </div>
        )}

        {/* Linked Records */}
        <div className="space-y-2">
          <Label>Linked Records</Label>
          <SearchRecordLookup
            value={null}
            onChange={(record) => {
              if (
                record &&
                !selectedLinkedRecords.find(
                  (r) =>
                    r.data.objectType === record.data.objectType &&
                    r.data.objectId === record.data.objectId
                )
              ) {
                setSelectedLinkedRecords([...selectedLinkedRecords, record]);
              }
            }}
            objectType={['item', 'entity', 'collection', 'initiative']}
            placeholder="Search for records to link..."
          />
          {selectedLinkedRecords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {selectedLinkedRecords.map((record) => (
                <div
                  key={`${record.data.objectType}:${record.data.objectId}`}
                  className="flex items-center gap-2 bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-sm"
                >
                  <span className="text-xs text-muted-foreground capitalize">
                    {record.data.objectType}
                  </span>
                  <span>{record.label}</span>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedLinkedRecords(
                        selectedLinkedRecords.filter(
                          (r) =>
                            !(
                              r.data.objectType === record.data.objectType &&
                              r.data.objectId === record.data.objectId
                            )
                        )
                      )
                    }
                    className="hover:text-destructive cursor-pointer"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            Link this initiative to other records (entities, items, collections, initiatives)
          </p>
        </div>

        {/* Linked Hubs */}
        <HubLinker selectedHubs={selectedHubs} onSelectedHubsChange={setSelectedHubs} />

        <div className="space-y-2">
          <Label>Full Content / Rules / Details (Markdown)</Label>
          <div data-color-mode="light" className="border rounded-md overflow-hidden">
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  value={field.value || ''}
                  onChange={(value) => field.onChange(value || '')}
                  height={400}
                  placeholder="Write your content... (supports markdown)"
                  enableImageInsert
                />
              )}
            />
          </div>
        </div>

        <div className="space-y-2">
          <MediaSelector
            value={image}
            onChange={(url) => setValue('image', typeof url === 'string' ? url : url?.[0] || null)}
            label="Cover Image"
            size="md"
          />
        </div>

        {/* Geo */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-lg">Geo</h3>

          {/* Address input */}
          <div className="space-y-2">
            <Label>Address</Label>
            <Controller
              name="address_1"
              control={control}
              render={({ field }) => {
                const currentAddress = field.value;
                const displayAddress =
                  currentAddress?.display_address || currentAddress?.formatted_address;
                if (displayAddress) {
                  return (
                    <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                      <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{displayAddress as string}</span>
                      <button
                        type="button"
                        onClick={() => field.onChange(null)}
                        className="shrink-0 cursor-pointer text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  );
                }
                return (
                  <PlaceAutocomplete
                    placeholder="Search for an address..."
                    onPlaceDetail={(detail) =>
                      field.onChange(detail ? (detail as unknown as Record<string, unknown>) : null)
                    }
                  />
                );
              }}
            />
          </div>

          {/* Show Address switch */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="showAddress">Show Address</Label>
              <p className="text-sm text-muted-foreground">
                Display the address on the initiative detail
              </p>
            </div>
            <Controller
              name="showAddress"
              control={control}
              render={({ field }) => (
                <Switch id="showAddress" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          {/* Show Map switch — only when address has coordinates */}
          {address_1?.geometry && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="showMap">Show Map</Label>
                <p className="text-sm text-muted-foreground">
                  Display an embedded map on the initiative detail
                </p>
              </div>
              <Controller
                name="showMap"
                control={control}
                render={({ field }) => (
                  <Switch id="showMap" checked={field.value} onCheckedChange={field.onChange} />
                )}
              />
            </div>
          )}
        </div>
        {/* Contact Details */}
        <div className="space-y-3 border-t pt-4">
          <h3 className="font-medium text-lg">Contact Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">Contact Person</Label>
              <Input
                id="contactName"
                type="text"
                {...register('contactName')}
                placeholder="John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactEmail">Email</Label>
              <Input
                id="contactEmail"
                type="email"
                {...register('contactEmail')}
                placeholder="contact@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactPhone">Phone</Label>
              <Input
                id="contactPhone"
                type="tel"
                {...register('contactPhone')}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactWebsite">Website</Label>
              <Input
                id="contactWebsite"
                type="url"
                {...register('contactWebsite')}
                placeholder="https://example.com"
              />
            </div>
          </div>

          {/* Hide Contact Details Switch */}
          {contactEmail && (
            <div className="flex items-center justify-between pt-2">
              <div className="space-y-0.5">
                <Label htmlFor="hideContactDetails">Don't show contact details</Label>
                <p className="text-sm text-muted-foreground">
                  Only the Send Enquiry form will be displayed
                </p>
              </div>
              <Controller
                name="hideContactDetails"
                control={control}
                render={({ field }) => (
                  <Switch
                    id="hideContactDetails"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          )}

          {/* Contact Visibility */}
          <Controller
            name="contactVisibility"
            control={control}
            render={({ field }) => (
              <AccessSelect value={field.value} onChange={field.onChange} label="Visible To" />
            )}
          />
        </div>

        {/* Engagement Settings */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-lg">Engagement</h3>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowReactions">Reactions</Label>
              <p className="text-sm text-muted-foreground">
                Allow users to react to this initiative
              </p>
            </div>
            <Controller
              name="allowReactions"
              control={control}
              render={({ field }) => (
                <Switch
                  id="allowReactions"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="allowComments">Comments</Label>
              <p className="text-sm text-muted-foreground">Enable commenting on this initiative</p>
            </div>
            <Controller
              name="allowComments"
              control={control}
              render={({ field }) => (
                <Switch id="allowComments" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="votingEnabled">Voting (Up / Down)</Label>
              <p className="text-sm text-muted-foreground">
                Allow users to vote up or down on this initiative
              </p>
            </div>
            <Controller
              name="votingEnabled"
              control={control}
              render={({ field }) => (
                <Switch id="votingEnabled" checked={field.value} onCheckedChange={field.onChange} />
              )}
            />
          </div>

          {/* Interest Mode */}
          <div className="space-y-2">
            <Label>Interest</Label>
            <Controller
              name="interestMode"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full md:w-64">
                    <SelectValue placeholder="Select interest mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disabled">Disabled</SelectItem>
                    <SelectItem value="simple">Simple (flag interest)</SelectItem>
                    <SelectItem value="structured">Detailed</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-sm text-muted-foreground">
              {watch('interestMode') === 'disabled'
                ? 'Users cannot flag interest'
                : watch('interestMode') === 'simple'
                  ? 'Users can mark themselves as interested'
                  : 'Users specify which entities they represent and can add a note'}
            </p>
          </div>

          {/* Structured Interest Config */}
          {watch('interestMode') === 'structured' && (
            <div className="space-y-4 pl-4 border-l-2 border-gray-200">
              {/* Options Table */}
              <div className="border rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left font-medium px-3 py-2">Name</th>
                      <th className="text-center font-medium px-3 py-2 w-20">Enabled</th>
                      <th className="text-center font-medium px-3 py-2 w-20">Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Entity Selection */}
                    <tr className="border-b">
                      <td className="px-3 py-2">
                        <div>
                          <span className="font-medium">Entity Selection</span>
                          <p className="text-xs text-muted-foreground">
                            Show entity selection when registering interest
                          </p>
                        </div>
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.enableEntitySelection"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="enableEntitySelection"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          )}
                        />
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.requireEntity"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="requireEntity"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!watch('structuredInterestConfig.enableEntitySelection')}
                            />
                          )}
                        />
                      </td>
                    </tr>
                    {/* Multiple Entities */}
                    {watch('structuredInterestConfig.enableEntitySelection') && (
                      <tr className="border-b">
                        <td className="px-3 py-2">
                          <div>
                            <span className="font-medium">Allow Multiple Entities</span>
                            <p className="text-xs text-muted-foreground">
                              Users can select more than one entity
                            </p>
                          </div>
                        </td>
                        <td className="text-center px-3 py-2">
                          <Controller
                            name="structuredInterestConfig.allowMultipleEntities"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                id="allowMultipleEntities"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          />
                        </td>
                        <td className="text-center px-3 py-2">
                          {/* No required toggle for this option */}
                        </td>
                      </tr>
                    )}
                    {/* Note */}
                    <tr className="border-b">
                      <td className="px-3 py-2">
                        <div>
                          <span className="font-medium">Note</span>
                          <p className="text-xs text-muted-foreground">
                            Users can add a note with their interest
                          </p>
                        </div>
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.allowNote"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="allowNote"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          )}
                        />
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.requireNote"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="requireNote"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!watch('structuredInterestConfig.allowNote')}
                            />
                          )}
                        />
                      </td>
                    </tr>
                    {/* Brand / Business */}
                    <tr className="border-b">
                      <td className="px-3 py-2">
                        <div>
                          <span className="font-medium">Brand / Business</span>
                          <p className="text-xs text-muted-foreground">
                            Text field for brand or business name
                          </p>
                        </div>
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.enableBrandField"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="enableBrandField"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          )}
                        />
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.requireBrandField"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="requireBrandField"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!watch('structuredInterestConfig.enableBrandField')}
                            />
                          )}
                        />
                      </td>
                    </tr>
                    {/* Timeframe */}
                    <tr>
                      <td className="px-3 py-2">
                        <div>
                          <span className="font-medium">Timeframe</span>
                          <p className="text-xs text-muted-foreground">
                            Urgency / when they need this
                          </p>
                        </div>
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.enableTimeframe"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="enableTimeframe"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          )}
                        />
                      </td>
                      <td className="text-center px-3 py-2">
                        <Controller
                          name="structuredInterestConfig.requireTimeframe"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              id="requireTimeframe"
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={!watch('structuredInterestConfig.enableTimeframe')}
                            />
                          )}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Custom Labels */}
              {watch('structuredInterestConfig.enableBrandField') && (
                <div className="space-y-2">
                  <Label htmlFor="brandFieldLabel">Brand / Business Label</Label>
                  <Input
                    id="brandFieldLabel"
                    {...register('structuredInterestConfig.brandFieldLabel')}
                    placeholder="Brand / Business (leave blank for default)"
                  />
                </div>
              )}

              {watch('structuredInterestConfig.enableTimeframe') && (
                <div className="space-y-2">
                  <Label htmlFor="timeframeLabel">Timeframe Label</Label>
                  <Input
                    id="timeframeLabel"
                    {...register('structuredInterestConfig.timeframeLabel')}
                    placeholder="Timeframe (leave blank for default)"
                  />
                </div>
              )}

              {/* Timeframe Options Editor */}
              {watch('structuredInterestConfig.enableTimeframe') && (
                <div className="space-y-2">
                  <Label>Timeframe Options</Label>
                  <p className="text-xs text-muted-foreground">
                    Customise the dropdown options for timeframe selection
                  </p>
                  <Controller
                    name="structuredInterestConfig.timeframeOptions"
                    control={control}
                    render={({ field }) => (
                      <div className="space-y-2">
                        {(field.value || DEFAULT_TIMEFRAME_OPTIONS).map((option, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <Input
                              value={option.label}
                              onChange={(e) => {
                                const current = field.value || DEFAULT_TIMEFRAME_OPTIONS;
                                const updated = [...current];
                                updated[index] = {
                                  ...updated[index],
                                  label: e.target.value,
                                  value: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                                };
                                field.onChange(updated);
                              }}
                              placeholder="Option label"
                              className="flex-1"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const current = field.value || DEFAULT_TIMEFRAME_OPTIONS;
                                const updated = current.filter((_, i) => i !== index);
                                field.onChange(updated);
                              }}
                              className="text-muted-foreground hover:text-destructive cursor-pointer"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const current = field.value || DEFAULT_TIMEFRAME_OPTIONS;
                            field.onChange([...current, { value: '', label: '' }]);
                          }}
                          className="cursor-pointer"
                        >
                          Add Option
                        </Button>
                      </div>
                    )}
                  />
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-gray-100">
                <Label htmlFor="dialogTitle">Dialog Title</Label>
                <Input
                  id="dialogTitle"
                  {...register('structuredInterestConfig.dialogTitle')}
                  placeholder="e.g. Register Interest (leave blank for default)"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dialogDescription">Dialog Description</Label>
                <Input
                  id="dialogDescription"
                  {...register('structuredInterestConfig.dialogDescription')}
                  placeholder="e.g. Let us know you're interested and we'll be in touch"
                />
              </div>
            </div>
          )}
        </div>

        {/* Attendance / Ticketing */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="font-medium text-lg">Attendance / Ticketing</h3>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="attendanceEnabled">Attendance</Label>
              <p className="text-sm text-muted-foreground">
                Allow users to register attendance for this initiative
              </p>
            </div>
            <Controller
              name="attendanceSettings.enabled"
              control={control}
              render={({ field }) => (
                <Switch
                  id="attendanceEnabled"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>

          {watch('attendanceSettings.enabled') && (
            <div className="space-y-3 pl-4 border-l-2 border-gray-200">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="attendanceRequiresApproval">Requires Approval</Label>
                  <p className="text-sm text-muted-foreground">
                    Registrations need host approval before being confirmed
                  </p>
                </div>
                <Controller
                  name="attendanceSettings.requiresApproval"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="attendanceRequiresApproval"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="attendanceCapacity">Capacity</Label>
                <p className="text-sm text-muted-foreground">
                  Maximum number of attendees. Leave empty for unlimited.
                </p>
                <Controller
                  name="attendanceSettings.capacity"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="attendanceCapacity"
                      type="number"
                      min={1}
                      placeholder="Unlimited"
                      className="w-full md:w-40"
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === '' ? null : parseInt(val, 10));
                      }}
                    />
                  )}
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="attendanceShowBlock">Show Registration Block</Label>
                  <p className="text-sm text-muted-foreground">
                    Display a prominent registration card on the detail page
                  </p>
                </div>
                <Controller
                  name="attendanceSettings.showRegistrationBlock"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="attendanceShowBlock"
                      checked={field.value !== false}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label>Block Position</Label>
                <Controller
                  name="attendanceSettings.blockPosition"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value || 'sidebar'} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full md:w-48">
                        <SelectValue placeholder="Position" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sidebar">Sidebar</SelectItem>
                        <SelectItem value="main">Main Content</SelectItem>
                        <SelectItem value="both">Both</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="attendanceBlockHeading">Block Heading</Label>
                <Controller
                  name="attendanceSettings.blockHeading"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="attendanceBlockHeading"
                      placeholder="Registration"
                      className="w-full md:w-64"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="attendanceBlockDescription">Custom Description</Label>
                <Controller
                  name="attendanceSettings.blockDescription"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="attendanceBlockDescription"
                      placeholder="Welcome! To join the event, please register below."
                      className="w-full"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                    />
                  )}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="attendanceButtonLabel">Button Label</Label>
                <Controller
                  name="attendanceSettings.buttonLabel"
                  control={control}
                  render={({ field }) => (
                    <Input
                      id="attendanceButtonLabel"
                      placeholder={
                        watch('attendanceSettings.requiresApproval')
                          ? 'Request to Join'
                          : 'One-Click RSVP'
                      }
                      className="w-full md:w-64"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value || undefined)}
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>

        {/* Gallery Media */}
        <div className="space-y-3 border-t pt-4">
          <Controller
            name="galleryImages"
            control={control}
            render={({ field }) => (
              <ImageGalleryManager
                images={field.value}
                onImagesChange={field.onChange}
                title="Gallery Media"
                description="Add images to showcase your initiative"
                emptyStateMessage="No images added yet"
                gridCols={3}
              />
            )}
          />
        </div>

        {/* Attendees */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h3 className="font-medium text-lg">Attendees</h3>
              <p className="text-sm text-muted-foreground">Specify attendee details</p>
            </div>
            <Controller
              name="specifyDemographics"
              control={control}
              render={({ field }) => (
                <Switch
                  id="specifyDemographics"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          {watch('specifyDemographics') && (
            <>
              <div className="space-y-2">
                <Label>Visibility</Label>
                <Controller
                  name="demographicsVisibility"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="w-full md:w-64">
                        <SelectValue placeholder="Select visibility" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="private">Private</SelectItem>
                        <SelectItem value="approved_viewers">Approved Viewers</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <Controller
                name="demographics"
                control={control}
                render={({ field }) => (
                  <DemographicsSection value={field.value} onChange={field.onChange} />
                )}
              />
            </>
          )}
        </div>
        {/* Components */}
        <div className="space-y-2 border-t pt-4">
          <Controller
            name="components"
            control={control}
            render={({ field }) => (
              <ObjectComponentsEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </div>

        {/* Related Content */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <h3 className="font-medium text-lg">Related Content</h3>
              <p className="text-sm text-muted-foreground">
                Show related content on the initiative detail page filtered by tags
              </p>
            </div>
            <Controller
              name="relatedContentEnabled"
              control={control}
              render={({ field }) => (
                <Switch
                  id="relatedContentEnabled"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          {watch('relatedContentEnabled') && (
            <div className="space-y-2 pl-4 border-l-2 border-gray-200">
              <Label htmlFor="relatedContentTitle">Section Title</Label>
              <Input
                id="relatedContentTitle"
                {...register('relatedContentTitle')}
                placeholder="Related Content"
              />
              <Label htmlFor="relatedContentBlurb">Intro / Blurb</Label>
              <Input
                id="relatedContentBlurb"
                {...register('relatedContentBlurb')}
                placeholder="Optional short intro text shown above the content"
              />
              <Label htmlFor="relatedContentTags">Content Tags (comma separated)</Label>
              <Input
                id="relatedContentTags"
                {...register('relatedContentTags')}
                placeholder="e.g. summer, campaign-2024, nz-market"
              />
              <p className="text-sm text-muted-foreground">
                Only published content matching these tags will be shown
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-4">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saveMutation.isPending}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={handlePreview}
          disabled={saveMutation.isPending}
          className="cursor-pointer"
        >
          <Eye className="w-4 h-4 mr-2" />
          Preview
        </Button>
        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save Initiative'}
        </Button>
      </div>
    </form>
  );
}
